import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { insightService, type Insight, type InsightCreateInput, type InsightCollection, type InsightTag, type InsightCategory } from '@/services/insightService';
import { useAuth } from '@/contexts/AuthContext';
import { triggerFeedbackNudge } from '@/lib/feedbackNudge';
import toast from 'react-hot-toast';

export type { Insight, InsightCreateInput, InsightCollection, InsightTag, InsightCategory };

export function useInsights() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const uid = user?.id;

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: loadingInsights, error } = useInfiniteQuery({
        queryKey: ['insights', uid],
        queryFn: ({ pageParam = 0 }) => insightService.fetchAll(pageParam as number, 50),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => lastPage.length === 50 ? allPages.length * 50 : undefined,
        enabled: !!user,
    });

    const insights = data ? data.pages.flat() : [];

    const { data: collections = [], isLoading: loadingCollections } = useQuery({
        queryKey: ['insight_collections', uid],
        queryFn: insightService.fetchCollections,
        enabled: !!user,
    });

    const { data: tags = [], isLoading: loadingTags } = useQuery({
        queryKey: ['insight_tags', uid],
        queryFn: insightService.fetchTags,
        enabled: !!user,
    });

    const { data: categories = [], isLoading: loadingCategories } = useQuery({
        queryKey: ['insight_categories', uid],
        queryFn: insightService.fetchCategories,
        enabled: !!user,
    });

    const createMutation = useMutation({
        mutationFn: (input: InsightCreateInput) => insightService.create(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
            toast.success('Insight criado com sucesso!');
            triggerFeedbackNudge();
        },
        onError: () => {
            toast.error('Erro ao criar insight.');
        }
    });

    const createBatchMutation = useMutation({
        mutationFn: (inputs: InsightCreateInput[]) => insightService.createBatch(inputs),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
            triggerFeedbackNudge();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates, tagIds }: { id: string; updates: Partial<Pick<Insight, 'aiTitle' | 'aiSummary' | 'aiCategory' | 'aiSentiment' | 'userNote' | 'isPinned' | 'collectionId'>>, tagIds?: string[] }) =>
            insightService.update(id, updates, tagIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
            toast.success('Insight atualizado!');
        },
        onError: () => {
            toast.error('Erro ao atualizar insight.');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: insightService.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
            toast.success('Insight excluído!');
        },
        onError: () => {
            toast.error('Erro ao excluir insight.');
        }
    });

    const deleteBatchMutation = useMutation({
        mutationFn: insightService.deleteBatch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
        },
    });

    // Collection Mutations
    const createCollectionMutation = useMutation({
        mutationFn: ({ name, color, description }: { name: string; color: string; description?: string }) => insightService.createCollection(name, color, description),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_collections', uid] })
    });

    const updateCollectionMutation = useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Pick<InsightCollection, 'name' | 'color' | 'description'>> }) => insightService.updateCollection(id, updates),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_collections', uid] })
    });

    const deleteCollectionMutation = useMutation({
        mutationFn: insightService.deleteCollection,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_collections', uid] })
    });

    // Tag Mutations
    const createTagMutation = useMutation({
        mutationFn: ({ name, color }: { name: string; color: string }) => insightService.createTag(name, color),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_tags', uid] })
    });

    const updateTagMutation = useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Pick<InsightTag, 'name' | 'color'>> }) => insightService.updateTag(id, updates),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_tags', uid] })
    });

    const deleteTagMutation = useMutation({
        mutationFn: insightService.deleteTag,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_tags', uid] })
    });

    // Category Mutations
    const createCategoryMutation = useMutation({
        mutationFn: ({ name, emoji, color }: { name: string; emoji: string; color: string }) => insightService.createCategory(name, emoji, color),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_categories', uid] })
    });

    const updateCategoryMutation = useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Pick<InsightCategory, 'name' | 'emoji' | 'color'>> }) => insightService.updateCategory(id, updates),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight_categories', uid] })
    });

    const deleteCategoryMutation = useMutation({
        mutationFn: insightService.deleteCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['insight_categories', uid] });
            queryClient.invalidateQueries({ queryKey: ['insights', uid] });
        }
    });

    return {
        insights,
        collections,
        tags,
        categories,
        loading: loadingInsights || loadingCollections || loadingTags || loadingCategories,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        createInsight: createMutation.mutateAsync,
        createInsightsBatch: createBatchMutation.mutateAsync,
        updateInsight: (id: string, updates: Partial<Pick<Insight, 'aiTitle' | 'aiSummary' | 'aiCategory' | 'aiSentiment' | 'userNote' | 'isPinned' | 'collectionId'>>, tagIds?: string[]) =>
            updateMutation.mutateAsync({ id, updates, tagIds }),
        deleteInsight: deleteMutation.mutateAsync,
        deleteInsightsBatch: deleteBatchMutation.mutateAsync,
        
        createCollection: createCollectionMutation.mutateAsync,
        updateCollection: (id: string, updates: Partial<Pick<InsightCollection, 'name' | 'color' | 'description'>>) => updateCollectionMutation.mutateAsync({ id, updates }),
        deleteCollection: deleteCollectionMutation.mutateAsync,

        createTag: createTagMutation.mutateAsync,
        updateTag: (id: string, updates: Partial<Pick<InsightTag, 'name' | 'color'>>) => updateTagMutation.mutateAsync({ id, updates }),
        deleteTag: deleteTagMutation.mutateAsync,

        createCategory: createCategoryMutation.mutateAsync,
        updateCategory: (id: string, updates: Partial<Pick<InsightCategory, 'name' | 'emoji' | 'color'>>) => updateCategoryMutation.mutateAsync({ id, updates }),
        deleteCategory: deleteCategoryMutation.mutateAsync,
    };
}

